import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons/faCaretRight';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { DropdownMenuItem } from '../dropdown-menu-item';
import { LockIconWithTooltip } from '../../../feature-gates/lock-icon-with-tooltip';

import style from './dropdown-menu-item-with-nested-menu.module.less';

/**
 * This component is used to create a DropdownMenuItem component that has a nested menu.
 * For this to work properly, the DropdownMenu component must have overflow unset
 *
 * ```
 * .dropdown-menu {
 *   overflow: unset !important;
 * }
 * ```
 * @param {object} props
 * @param {string|object} props.title - the button text
 * @param {node} props.children - the nested menu items to be shown on hover
 * @param {string} props.className - additional class names to be added to the component
 * @param {boolean} props.isLocked - show lock icon and disable the menu item and hide children
 * @returns {ReactElement}
 */
export const DropdownMenuItemWithNestedMenu = ({ children = null, className = '', title, isLocked = false }) => {
  const classes = classnames(className, style['dropdown-menu-item']);
  return (
    <DropdownMenuItem className={classes} disabled={isLocked}>
      <div className={style['dropdown-title']}>
        <div className={style['dropdown-title-name']}>{title}</div>
        <div
          className={classnames(style['dropdown-top-level-svg'], {
            [style['lock-icon-wrapper']]: isLocked,
          })}
        >
          {!isLocked && <FontAwesomeIcon icon={faCaretRight} />}
          {isLocked && <LockIconWithTooltip iconClass={style['lock-icon']} />}
        </div>
        <div />
      </div>
      {!isLocked && <div className={style['dropdown-menu-items']}>{children}</div>}
    </DropdownMenuItem>
  );
};

DropdownMenuItemWithNestedMenu.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  isLocked: PropTypes.bool,
};
